import { useEffect, useState } from 'react';
import { Flex } from 'antd';
import AddTransactionCard from './addTransactionCard';
import TransactionTypeCategoryHeader from './transactionTypeCategoryHeader';
import AddEditTransactionHeader from './addEditTransactionHeader';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../../context/PageContext';
import { useLocation } from 'react-router-dom';
import NoData from '../noData';
import { InfoCircle, Slash, TransactionTypeEmpty } from '../../icons';
import { ITransactionTypes } from '../../../interface/transactionTypes';
import { ITransactionTypeGroup } from './groupTransactionTypesByCategory';
import { buildTransactionTypeSelection } from './transactionTypeSelection';
import { useTransaction } from '../../../context/TransactionContext';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { getTranslationFromTranslatableEntity, InvoiceableType } from '../../../constants/matter';
import { Spin, Text } from '../../Ant';
import { useTimer } from '../../../context/TimerContext';
import { ITimer } from '../../../interface/timer';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../../hooks/useEscapeToGoBack';
import { useUser } from '../../../context/UserContext';
import { UserType } from '../../../constants/constants';
import { MessageContainer } from '../../../assets/styles/components/dashboard.styled';
import { useLanguage } from '../../../context/LanguageContext';
import SortSelector from '../../sortPopover/sortSelector';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import { useTransactionTypeList } from '../../../hooks/useTransactionTypeList';
import { useClearTransactionTypeFilter } from './useClearTransactionTypeFilter';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { useDiscardDialog } from '../../../hooks/useDiscardDialog';
import { isTransactionFormChanged } from './isTransactionFormChanged';
import { isTimerFormChanged } from '../../timer/isTimerFormChanged';
import CustomModal from '../../informationPopup/customModal';

const TransactionCardList = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { user } = useUser();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const location = useLocation();
  const { updateTransaction, transaction, initialTransaction } = useTransaction();
  const { timer, updateTimer, initialTimer } = useTimer();
  const isTimerRoute = location?.state?.isFromTimer ?? false;
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();

  const transactionTypeList = useTransactionTypeList();
  const {
    setSearchValue,
    selectedFilter,
    setSelectedFilter,
    transactionTypesFilterOptions,
    disabledTransactionTypesFilterValues,
    disableFilter,
    filterDisabledInfoMessage,
    transactionTypeSortOptions,
    activeSort,
    setActiveSort,
    transactionTypeGroups,
    categoryFilterOptions,
    selectedCategoryFilters,
    setSelectedCategoryFilters,
    isTransactionTypeLoading,
    isFetching,
    hasNextPage,
    ref,
    onlyHourBasedTransactionTypes,
    refreshTransactionTypeList,
  } = transactionTypeList;
  const handleClearFilter = useClearTransactionTypeFilter(transactionTypeList);

  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set());

  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true ||
    user?.userType === UserType.RESTRICTED ||
    transaction?.matter?.invoiceableType === InvoiceableType.internal;

  const handleTransaction = (type: ITransactionTypes, subject: string) => {
    updateTransaction(buildTransactionTypeSelection(transaction, type, subject));
    goBackWithSelection();
  };
  const handleTimer = (type: ITransactionTypes) => {
    const payload = {
      ...timer,
      transactionType: type,
      transactionTypeId: type?.id,
      isTransactionTypeManuallyAdded: true,
      ...(!timer?.isSubjectManuallyAdded && {
        subject: getTranslationFromTranslatableEntity(
          type?.translations || [],
          getAppLanguageCode(),
        ),
      }),
    } as ITimer;
    updateTimer(payload);
    goBackWithSelection();
  };
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    if (isTimerRoute) {
      promptDiscardIfChanged(isTimerFormChanged(timer, initialTimer, !!timer?.id), () => {
        updateTimer(null);
        goBack(undefined, 2);
      });
      return;
    }
    promptDiscardIfChanged(
      isTransactionFormChanged(transaction, initialTransaction, !!transaction?.id),
      () => {
        updateTransaction(null);
        goBack(undefined, 2);
      },
    );
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('transaction:selectTransactionType')}
        showCross
      />,
    );
  }, []);
  const groupIds = transactionTypeGroups.map((group) => group.id);
  const areAllGroupsExpanded = !groupIds.some((id) => collapsedGroupIds.has(id));
  const toggleAllGroups = () =>
    setCollapsedGroupIds(areAllGroupsExpanded ? new Set(groupIds) : new Set());
  const toggleGroup = (groupId: string) => {
    setCollapsedGroupIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(groupId)) {
        next.add(groupId);
      }
      return next;
    });
  };
  const renderTransactionTypeCard = (type: ITransactionTypes) => (
    <AddTransactionCard
      hidden={hidePricingInfo}
      key={type?.id}
      transactionType={type}
      onClick={(type: ITransactionTypes, subject: string) => {
        if (isTimerRoute) {
          handleTimer(type);
        } else {
          handleTransaction(type, subject);
        }
      }}
    />
  );
  const renderTransactionTypeGroup = (group: ITransactionTypeGroup) => {
    const isExpanded = !collapsedGroupIds.has(group.id);
    const contentId = `category-group-${group.id}`;
    return (
      <Flex vertical key={group.id} className="w-full gap-date-group">
        <TransactionTypeCategoryHeader
          name={group.name}
          count={group.transactionTypes.length}
          expanded={isExpanded}
          contentId={contentId}
          onToggle={() => toggleGroup(group.id)}
        />
        {isExpanded && (
          // display: contents lets the cards share the group's own gap.
          <div id={contentId} className="d-contents">
            {group.transactionTypes.map(renderTransactionTypeCard)}
          </div>
        )}
      </Flex>
    );
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="transaction-type-listing"
        className="transaction-card-container"
        contentClassName="w-full"
        header={
          <div className="d-flex flex-col gap-10 w-full">
            {onlyHourBasedTransactionTypes && (
              <MessageContainer align="flex-start" justify="flex-start" gap={10}>
                <InfoCircle />
                <Text $fontsize="13px" $color="title" $lineheight="16px" $weight={500}>
                  {t('transaction:onlyTransactionTypesWithUnitHourCanBeSelected')}
                </Text>
              </MessageContainer>
            )}
            <SearchInputWithFilters
              autoFocus
              filtersDisabledInfoMessage={filterDisabledInfoMessage}
              disableFilter={disableFilter}
              disabledRadioValues={disabledTransactionTypesFilterValues}
              handleSearch={(value) => {
                setSearchValue(value);
              }}
              handleRefresh={refreshTransactionTypeList}
              searchTitle={t('transaction:searchTransactionTypes')}
              radioOptions={transactionTypesFilterOptions}
              filterTitle={t('transaction:transactionType.filter.label')}
              checkboxOptions={categoryFilterOptions}
              checkboxTitle={
                categoryFilterOptions.length > 0
                  ? t('transaction:transactionType.category')
                  : undefined
              }
              selectedFilter={[selectedFilter, ...selectedCategoryFilters]}
              setSelectedFilter={(val) => setSelectedFilter(val)}
              setSelectedCheckboxFilter={setSelectedCategoryFilters}
              clearFilter={handleClearFilter}
              dateGroupToggle={
                groupIds.length > 0
                  ? { areAllExpanded: areAllGroupsExpanded, onToggleAll: toggleAllGroups }
                  : undefined
              }
            >
              <SortSelector
                modalLabel={t('common:sorting.title')}
                options={transactionTypeSortOptions}
                onApply={(sort) => setActiveSort(sort)}
                activeSort={activeSort}
              />
            </SearchInputWithFilters>
          </div>
        }
      >
        {transactionTypeGroups.map(renderTransactionTypeGroup)}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {transactionTypeGroups.length === 0 && !isTransactionTypeLoading && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('transaction:noTransactionTypes')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<TransactionTypeEmpty />}
            />
          </div>
        )}
        {isTransactionTypeLoading ? <SkeletonCardList rows={[1, 2, 2]} /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default TransactionCardList;
